/**
 * SPACES (RFC 0281 §7): named, ordered, switchable arrangements on the World.
 *
 * A space is a name, an order, and a tree. The tree is the CONTENT URL string
 * (`encodeContent`, RFC 0266), not JSON: it reuses the encoder and the validated
 * decoder the layout already has, it is legible when a person opens the
 * document, and it is small — which matters, because it is rewritten every time
 * a seam moves.
 *
 * ITS OWN DOCUMENT, pointed at from the World. This is the split RFC 0261
 * already made for presence, for the same reason: the project directory is
 * stable and a layout is rewritten on every rearrangement, so folding that churn
 * into the World would put permanent weight on the one document every client
 * opens first. `WorldDoc.spacesDocId` is the pointer, alongside `presenceDocId`.
 *
 * THE TREE IS A SCALAR AND MUST NEVER BECOME A TEXT FIELD. Two devices in one
 * space can write different trees, and Automerge merges two text fields
 * character by character into a string that decodes to nonsense. Replaced
 * wholesale, last writer wins per write, which is a layout you did not choose
 * rather than a layout nobody could have chosen.
 *
 * AND IT WAS ONE. In Automerge 3 a plain string assigned into a document IS a
 * text field: every assignment wrote one op per character. On Oct 12 the
 * spaces document held 1.17 million ops, loaded in the daemon, the window's
 * worker and its tab. So the tree is stored as an `ImmutableString`, one op a
 * write, and a write of the tree already there is skipped. Old entries are
 * text and read the same through `String()`.
 *
 * Everything here is self-contained on purpose: the shapes, the projections and
 * the mutations sit together rather than being spread across `doc-shapes.ts`,
 * `reads.ts` and `mutations/`, because the whole of spaces is one small idea and
 * splitting it three ways buys nothing.
 */

import * as A from '@automerge/automerge/slim'
import type {
  AnyDocumentId,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'
import { createPersonOwnedDocument } from '../project/creation'

/** An Automerge draft, untyped — the same shape `mutations/world.ts` uses. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any

/** One space (RFC 0281 §7). */
export interface SpaceEntry {
  name: string
  /** The content tree in the url grammar, encoded by `encodeContent`. */
  content: string
  /**
   * Index into visual leaf order: where the caret lands on switch.
   *
   * An INDEX, not a leaf id. Leaf ids are deliberately absent from the encoding
   * (RFC 0266: the url carries no internal addressing), so every decode mints
   * fresh ones and an id stored here would name nothing after a round trip.
   */
  focus?: number
  /** Whether the sidebar row is expanded. Per space, not per device (§7). */
  collapsed?: boolean
  /**
   * The layout this space is arranged by (RFC 0284 §4e, S4b).
   *
   * An app document id, naming a catalog entry with `presentation: 'layout'`.
   * Normally in the World's catalog (RFC 0295), because chrome is not a
   * project's.
   *
   * ABSENT MEANS THE BUILT-IN DEFAULT, which is every space written before this
   * and every space nobody has rearranged. A NAMED LAYOUT THAT RESOLVES TO
   * NOTHING means the same thing rather than an error: a layout can be
   * uninstalled while a space still points at it, and a window that refused to
   * draw in that case would leave a person with no way to pick a different one.
   */
  layoutId?: string
  updatedAt: number
}

/** The spaces document: what `WorldDoc.spacesDocId` points at. */
export interface SpacesDoc {
  spaces: Record<string, SpaceEntry>
  /** Sidebar order, and what `ctrl+1..9` mean. */
  order: string[]
}

/** A space as a switcher renders it: the entry, plus the id that names it. */
export interface SpaceRef extends SpaceEntry {
  spaceId: string
}

/**
 * Project the spaces document into the switcher's list, in `order`.
 *
 * ORDER IS THE SOURCE OF TRUTH AND IS NOT TRUSTED. `order` as a list of ids
 * merges badly under concurrent reorder (§10): two devices can leave it holding
 * a duplicate, or an id whose entry was deleted, or miss one that was added.
 * Rather than repair the document on read, this deduplicates, drops ids with no
 * entry, and APPENDS any entry `order` forgot — so a space created on another
 * device is listed even before the two orders reconcile, which is the failure
 * that would otherwise look like losing a space.
 *
 * Accepts the raw snapshot and returns `[]` for an absent or empty document, so
 * a client whose World has no spaces yet renders an empty strip rather than
 * throwing.
 */
export function projectSpaces(spacesDoc: unknown): SpaceRef[] {
  if (!spacesDoc || typeof spacesDoc !== 'object') return []
  const raw = spacesDoc as { spaces?: unknown; order?: unknown }
  const spaces =
    raw.spaces && typeof raw.spaces === 'object'
      ? (raw.spaces as Record<string, unknown>)
      : {}

  const entryAt = (id: string): SpaceRef | null => {
    const value = spaces[id]
    if (!value || typeof value !== 'object') return null
    const entry = value as Partial<SpaceEntry>
    return {
      spaceId: id,
      name: String(entry.name ?? ''),
      content: String(entry.content ?? ''),
      ...(typeof entry.focus === 'number' ? { focus: entry.focus } : {}),
      ...(typeof entry.collapsed === 'boolean'
        ? { collapsed: entry.collapsed }
        : {}),
      // Carried through, and typed rather than trusted: a non-string here would
      // reach `resolveLayout` as an id and match nothing, which is the same as
      // absent, so narrowing costs nothing and says what is meant.
      ...(typeof entry.layoutId === 'string' && entry.layoutId
        ? { layoutId: entry.layoutId }
        : {}),
      updatedAt: typeof entry.updatedAt === 'number' ? entry.updatedAt : 0,
    }
  }

  const out: SpaceRef[] = []
  const seen = new Set<string>()

  for (const value of Array.isArray(raw.order) ? raw.order : []) {
    const id = String(value)
    if (seen.has(id)) continue
    const entry = entryAt(id)
    if (!entry) continue
    seen.add(id)
    out.push(entry)
  }

  // Whatever `order` did not name, oldest first, so an appended space lands
  // where it was made rather than jumping to the top of someone else's strip.
  const missing = Object.keys(spaces)
    .filter((id) => !seen.has(id))
    .map(entryAt)
    .filter((entry): entry is SpaceRef => entry !== null)
    .sort((a, b) => a.updatedAt - b.updatedAt)

  return [...out, ...missing]
}

/** One space by id, or null. `projectSpaces` for the list; this for a lookup. */
export function projectSpace(
  spacesDoc: unknown,
  spaceId: string | null | undefined,
): SpaceRef | null {
  if (!spaceId) return null
  return projectSpaces(spacesDoc).find((s) => s.spaceId === spaceId) ?? null
}

/** The World's spaces pointer, or null. Bare id, `automerge:` stripped. */
export function spacesDocId(worldDoc: unknown): string | null {
  if (!worldDoc || typeof worldDoc !== 'object') return null
  const value = (worldDoc as { spacesDocId?: unknown }).spacesDocId
  if (typeof value !== 'string' || value.length === 0) return null
  return value.replace(/^automerge:/, '')
}

/** Create an empty spaces document, owned by the person when sharing is on. */
export async function createSpacesDoc(
  repo: Repo,
): Promise<DocHandle<SpacesDoc>> {
  const handle = await createPersonOwnedDocument<SpacesDoc>(repo)
  handle.change((d) => {
    d.spaces = {}
    d.order = []
  })
  return handle
}

/**
 * Point a World at its spaces document.
 *
 * Separate from `createSpacesDoc` because the two writes are to two documents,
 * and a caller that creates one and fails to link it has an orphan rather than a
 * broken World.
 */
export async function setSpacesDoc(
  repo: Repo,
  worldId: string,
  docId: string,
): Promise<void> {
  const handle = await repo.find(worldId as AnyDocumentId)
  handle.change((d: Draft) => {
    d.spacesDocId = docId.replace(/^automerge:/, '')
  })
}

/**
 * Add a space, or replace the one already at `spaceId`.
 *
 * Appends to `order` only when the id is not already in it, so re-saving a space
 * keeps its position: a strip that reshuffled every time you rearranged a layout
 * would make `ctrl+2` mean something new every few minutes.
 */
export async function putSpace(
  repo: Repo,
  spacesDocId: string,
  spaceId: string,
  entry: Omit<SpaceEntry, 'updatedAt'> & { updatedAt?: number },
): Promise<void> {
  const handle = await repo.find(spacesDocId as AnyDocumentId)
  handle.change((d: Draft) => {
    if (!d.spaces) d.spaces = {}
    if (!d.order) d.order = []
    d.spaces[spaceId] = {
      name: entry.name,
      content: new A.ImmutableString(entry.content),
      ...(entry.focus !== undefined ? { focus: entry.focus } : {}),
      ...(entry.collapsed !== undefined ? { collapsed: entry.collapsed } : {}),
      ...(entry.layoutId !== undefined ? { layoutId: entry.layoutId } : {}),
      updatedAt: entry.updatedAt ?? Date.now(),
    }
    if (!d.order.includes(spaceId)) d.order.push(spaceId)
  })
}

/**
 * Write a space's tree, leaving its name and position alone.
 *
 * The hot path: every rearrangement calls this, debounced. A whole-entry write
 * would carry a stale name over a rename made on another device between the two.
 */
export async function setSpaceContent(
  repo: Repo,
  spacesDocId: string,
  spaceId: string,
  content: string,
  focus?: number,
): Promise<void> {
  const handle = await repo.find(spacesDocId as AnyDocumentId)
  handle.change((d: Draft) => {
    const entry = d.spaces?.[spaceId]
    if (!entry) return
    // ASSIGNED, never spliced, and as a scalar: see the header.
    const same = String(entry.content ?? '') === content
    if (same && (focus === undefined || entry.focus === focus)) return
    if (!same) entry.content = new A.ImmutableString(content)
    if (focus !== undefined) entry.focus = focus
    entry.updatedAt = Date.now()
  })
}

/**
 * Point a space at a layout, or at nothing (RFC 0284 S4b).
 *
 * `null` CLEARS rather than storing an empty string, so "I want the built-in
 * back" and "this layout is called ''" cannot be confused. Reading an absent
 * field and reading a cleared one are then the same code path.
 *
 * Not folded into `putSpace`: choosing a layout is a deliberate act on one
 * field, and a whole-entry write would carry a stale tree over whatever the
 * other device rearranged in between.
 */
export async function setSpaceLayout(
  repo: Repo,
  spacesDocId: string,
  spaceId: string,
  layoutId: string | null,
): Promise<void> {
  const handle = await repo.find(spacesDocId as AnyDocumentId)
  handle.change((d: Draft) => {
    const entry = d.spaces?.[spaceId]
    if (!entry) return
    if (layoutId) entry.layoutId = layoutId
    else delete entry.layoutId
    entry.updatedAt = Date.now()
  })
}

/** Rename a space. Its tree and its place in the strip are untouched. */
export async function renameSpace(
  repo: Repo,
  spacesDocId: string,
  spaceId: string,
  name: string,
): Promise<void> {
  const handle = await repo.find(spacesDocId as AnyDocumentId)
  handle.change((d: Draft) => {
    const entry = d.spaces?.[spaceId]
    if (!entry) return
    entry.name = name
    entry.updatedAt = Date.now()
  })
}

/**
 * Delete a space, from both the map and the order.
 *
 * Destroys nothing (§7's invariant): a space owns no content, so every session
 * and document it named is still in its project and the tree it held was a view.
 */
export async function deleteSpace(
  repo: Repo,
  spacesDocId: string,
  spaceId: string,
): Promise<void> {
  const handle = await repo.find(spacesDocId as AnyDocumentId)
  handle.change((d: Draft) => {
    if (d.spaces) delete d.spaces[spaceId]
    if (Array.isArray(d.order)) {
      const at = d.order.indexOf(spaceId)
      if (at !== -1) d.order.splice(at, 1)
    }
  })
}

/**
 * Move a space to a position in the strip, which is what `ctrl+1..9` index.
 *
 * Clamped rather than rejected: a drag past the end is a legitimate gesture
 * meaning "last", and an out-of-range index from a stale client should land
 * somewhere sensible instead of throwing.
 */
export async function reorderSpace(
  repo: Repo,
  spacesDocId: string,
  spaceId: string,
  toIndex: number,
): Promise<void> {
  const handle = await repo.find(spacesDocId as AnyDocumentId)
  handle.change((d: Draft) => {
    if (!Array.isArray(d.order)) return
    const from = d.order.indexOf(spaceId)
    // A space `order` has never heard of is still orderable, PROVIDED it
    // exists. `projectSpaces` appends entries the order forgot, so a space
    // created on another device is listed, and draggable, before the two orders
    // reconcile; refusing here would make that drag a silent no-op, the row
    // springing back with nothing to say why. An id with no entry is a
    // different thing and still refused, or a stale client could write names
    // into the order that nothing will ever draw.
    if (from === -1 && !d.spaces?.[spaceId]) return
    if (from !== -1) d.order.splice(from, 1)
    const to = Math.max(0, Math.min(toIndex, d.order.length))
    d.order.splice(to, 0, spaceId)
  })
}
